import { useEffect, useMemo, useRef } from "react";

import { t, TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TSTTable, TST_COLUMN_SIZE } from "@impero-com/ui";
import { createColumnHelper, Table } from "@tanstack/react-table";
import {
    Control,
    useFieldArray,
    UseFormReturn,
    UseFormWatch,
} from "react-hook-form";
import uuid from "uuid";

import { ControlProgramTagView } from "api/generated/model";

import { CreateNewTagNameInput } from "./components/CreateNewTagNameInput";
import { ControlProgramTagCategoryCreateFormSchema } from "./useControlProgramTagCategoryFormSchema";
import {
    COLUMN_ID_ACTION,
    COLUMN_ID_NAME,
    ControlProgramTagManagementOverview,
    useTable,
} from "./utils";

interface ControlProgramTagManagementCreateListProps {
    form: UseFormReturn<ControlProgramTagCategoryCreateFormSchema>;
    setTagNames: React.Dispatch<React.SetStateAction<string[]>>;
}
export function ControlProgramTagManagementCreateList({
    form,
    setTagNames,
}: ControlProgramTagManagementCreateListProps) {
    const { t } = useTranslation();
    const { control, watch, setValue, trigger, getValues } = form;
    const {
        fields: tags,
        prepend,
        remove,
    } = useFieldArray({
        control,
        name: "tags",
    });
    useEffect(() => {
        const names = tags.map((tag) => tag.name);
        setTagNames(names);
    }, [setTagNames, tags]);

    const handleAddTag = async () => {
        const newTagName = getValues("newTagName")?.trim();
        if (!newTagName) {
            return;
        }

        const isValid = await trigger("newTagName");
        if (!isValid) {
            return;
        }

        prepend({
            id: uuid.v4(),
            name: newTagName,
        });

        setValue("newTagName", "");
    };
    const handleDeleteTag = (tagIndex: number) => {
        remove(tagIndex);
    };

    const table = useControlProgramTagManagementListTable(
        control,
        tags,
        handleAddTag,
        handleDeleteTag,
        watch
    );
    const tableElement = useRef<HTMLDivElement>(null);

    return (
        <TSTTable
            tableRef={tableElement}
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

export function useControlProgramTagManagementListTable(
    control: Control<ControlProgramTagCategoryCreateFormSchema>,
    tags: Omit<ControlProgramTagView, "controlProgramTagCategoryId">[],
    handleAddTag: () => Promise<void>,
    handleDeleteTag: (tagIndex: number) => void,
    watch: UseFormWatch<ControlProgramTagCategoryCreateFormSchema>
): Table<ControlProgramTagManagementOverview> {
    const columns = useMemo(
        () => buildColumns(t, control, handleAddTag, handleDeleteTag, watch),
        [control, handleAddTag, handleDeleteTag, watch]
    );
    const table = useTable(tags, columns);
    return table;
}

function buildColumns(
    t: TFunction,
    control: Control<ControlProgramTagCategoryCreateFormSchema>,
    handleAddTag: () => Promise<void>,
    handleDeleteTag: (tagIndex: number) => void,
    watch: UseFormWatch<ControlProgramTagCategoryCreateFormSchema>
) {
    const columnHelper =
        createColumnHelper<ControlProgramTagManagementOverview>();
    return [
        columnHelper.accessor((row) => row.newTagName, {
            id: COLUMN_ID_NAME,
            header: t("TagManagementTable_Column_Name"),
            size: TST_COLUMN_SIZE.M,
            cell: (info) => {
                // Check if this is the first row
                if (info.row.index === 0) {
                    return (
                        <CreateNewTagNameInput
                            control={control}
                            handleAddTag={handleAddTag}
                            watch={watch}
                        />
                    );
                }
                return <span>{info.getValue()}</span>;
            },
        }),
        columnHelper.accessor((row) => row.action, {
            id: COLUMN_ID_ACTION,
            header: t("TagManagementTable_Column_Action"),
            size: TST_COLUMN_SIZE.XS,
            cell: (info) => {
                const tagIndex = info.row.index - 1; // Adjust for new tag row
                if (info.row.index === 0) {
                    return (
                        <a onClick={() => void handleAddTag()}>
                            {info.getValue()}
                        </a>
                    );
                }

                return (
                    <a onClick={() => handleDeleteTag(tagIndex)}>
                        {info.getValue()}
                    </a>
                );
            },
        }),
    ];
}
